import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchEpicTimeline, postEpicComment } from '../../../../lib/api/epicTimeline';
import {
  ACTIONS,
  OPERATOR_ACTIONS,
  type TimelineItem,
  type TimelineKind,
} from '../../../../../../shared/lib/epicActionCenter';
import { Button, Card, CardBody } from '../../../../components/ui';

// ---------------------------------------------------------------------------
// Activity tab — the GitHub issue thread, mirrored. Forge's ~60 comment types
// are classified server-side; routine progress is collapsed by default so the
// things that need a person stand out. A composer posts replies and commands.
// ---------------------------------------------------------------------------

type Filter = 'all' | 'attention' | 'people';

const KIND_STYLE: Record<TimelineKind, { label: string; chip: string; rail: string }> = {
  needs_you: { label: 'Needs you', chip: 'bg-warning-50 text-warning-700 border-warning-100', rail: 'border-warning-500' },
  problem: { label: 'Problem', chip: 'bg-danger-50 text-danger-700 border-danger-100', rail: 'border-danger-500' },
  success: { label: 'Done', chip: 'bg-success-50 text-success-700 border-success-100', rail: 'border-success-500' },
  progress: { label: 'Progress', chip: 'bg-slate-100 text-slate-600 border-slate-200', rail: 'border-slate-300' },
  info: { label: 'Forge', chip: 'bg-primary-50 text-primary-700 border-primary-100', rail: 'border-primary-300' },
  human: { label: 'Comment', chip: 'bg-slate-100 text-slate-700 border-slate-200', rail: 'border-slate-400' },
  command: { label: 'Command', chip: 'bg-slate-100 text-slate-700 border-slate-200', rail: 'border-slate-400' },
};

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

const Row: React.FC<{ item: TimelineItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  const s = KIND_STYLE[item.kind];
  const compact = item.kind === 'progress' || item.kind === 'command';
  return (
    <li
      data-testid={`timeline-${item.kind}`}
      className={`border-l-4 ${s.rail} bg-white rounded-md border border-slate-200 px-3 ${compact ? 'py-1.5' : 'py-2.5'}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-2xs uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded border ${s.chip}`}>{s.label}</span>
        <span className="text-sm font-medium text-slate-900">{item.title}</span>
        <span className="text-xs text-slate-500 ml-auto">
          {item.fromForge ? 'Forge' : item.author} · {when(item.createdAt)}
        </span>
      </div>
      {!compact && item.excerpt && (
        <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{open ? item.body : item.excerpt}</p>
      )}
      <div className="mt-1 flex gap-3 text-xs">
        {!compact && item.body.length > item.excerpt.length + item.title.length + 8 && (
          <button type="button" className="text-primary-600 hover:underline" onClick={() => setOpen((v) => !v)}>
            {open ? 'Show less' : 'Show full comment'}
          </button>
        )}
        <a href={item.htmlUrl} target="_blank" rel="noreferrer" className="text-slate-500 hover:underline">
          GitHub ↗
        </a>
      </div>
    </li>
  );
};

export const ActivityTab: React.FC<{ epicRef: string }> = ({ epicRef }) => {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['epic-timeline', epicRef],
    queryFn: () => fetchEpicTimeline(epicRef),
    staleTime: 10_000,
    refetchInterval: 20_000,
  });

  const [filter, setFilter] = useState<Filter>('all');
  const [showProgress, setShowProgress] = useState(false);
  const [text, setText] = useState('');

  const send = useMutation({
    mutationFn: (v: { text: string } | { action: keyof typeof ACTIONS; input?: string }) => postEpicComment(epicRef, v),
    onSuccess: () => {
      setText('');
      queryClient.invalidateQueries({ queryKey: ['epic-timeline', epicRef] });
    },
  });

  const visible = useMemo(() => {
    const items = q.data?.items ?? [];
    return items.filter((i) => {
      if (!showProgress && i.kind === 'progress') return false;
      if (filter === 'attention') return i.kind === 'needs_you' || i.kind === 'problem';
      if (filter === 'people') return i.kind === 'human' || i.kind === 'command';
      return true;
    });
  }, [q.data, filter, showProgress]);

  const hiddenProgress = (q.data?.items ?? []).filter((i) => i.kind === 'progress').length;
  const chips: Array<[Filter, string]> = [['all', 'All'], ['attention', 'Needs attention'], ['people', 'People']];

  return (
    <div className="space-y-4" data-testid="activity-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {chips.map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setFilter(id)}
            aria-pressed={filter === id}
            data-testid={`activity-filter-${id}`}
            className={`text-xs px-2.5 py-1 rounded-full border ${
              filter === id ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-slate-300 text-slate-600'
            }`}
          >
            {label}
          </button>
        ))}
        <label className="text-xs text-slate-600 flex items-center gap-1 ml-2">
          <input type="checkbox" checked={showProgress} onChange={(e) => setShowProgress(e.target.checked)} data-testid="activity-show-progress" />
          Show progress events{hiddenProgress > 0 ? ` (${hiddenProgress})` : ''}
        </label>
        {q.data && (
          <a href={q.data.issueUrl} target="_blank" rel="noreferrer" className="text-xs text-primary-600 hover:underline ml-auto">
            Open issue #{q.data.issueNumber} on GitHub ↗
          </a>
        )}
      </div>

      {q.isLoading && <div className="text-sm text-slate-500">Loading activity…</div>}
      {q.isError && (
        <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
          {(q.error as Error).message}
        </div>
      )}
      {q.isSuccess && visible.length === 0 && (
        <div className="text-sm text-slate-500" data-testid="activity-empty">Nothing here yet.</div>
      )}
      {q.isSuccess && visible.length > 0 && (
        <ol className="space-y-2" data-testid="activity-list">
          {visible.map((it) => <Row key={it.id} item={it} />)}
        </ol>
      )}

      <Card>
        <CardBody>
          <div className="space-y-2">
            <label htmlFor="activity-composer" className="text-xs font-medium text-slate-700">
              Reply on GitHub
            </label>
            <textarea
              id="activity-composer"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="Write a reply, or use a command chip below"
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              data-testid="activity-composer"
            />
            <div className="flex items-center gap-2 flex-wrap">
              {OPERATOR_ACTIONS.filter((k) => !ACTIONS[k].input).map((k) => (
                <Button
                  key={k}
                  size="sm"
                  variant="secondary"
                  disabled={send.isPending}
                  onClick={() => send.mutate({ action: k })}
                  title={ACTIONS[k].help}
                  data-testid={`activity-cmd-${k}`}
                >
                  {ACTIONS[k].command}
                </Button>
              ))}
              <Button
                size="sm"
                variant="primary"
                className="ml-auto"
                disabled={!text.trim() || send.isPending}
                loading={send.isPending}
                onClick={() => send.mutate({ text })}
                data-testid="activity-send"
              >
                Send
              </Button>
            </div>
            <p className="text-xs text-slate-500">Posted to the GitHub issue under the Forge app, signed with your email.</p>
            {send.isError && (
              <div role="alert" className="text-xs text-danger-700">{(send.error as Error).message}</div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
